// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import { ActionIcon, Button, Flex, Paper, Stack, Table, Text } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { InchesInput } from 'components/Mantine/InchesInput';
import { InfoIcon } from 'components/Mantine/InfoIcon';
import { NumberInputWrapped } from 'components/Mantine/NumberInputWrapped';
import { strongEntries } from 'helpers/strongEntries';
import {
  blankYbcRow,
  useBendCreatorFormContext,
} from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';
import { useBendCreatorValidationContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorValidationContext';

const MINIMUM_ROWS = 2;
const MAXIMUM_ROWS = 6;

export const BendYbcGrid = () => {
  const form = useBendCreatorFormContext();
  const { scheduleValidation, wrappedGetYBCRowInputProps } = useBendCreatorValidationContext();
  const [addedRowIndex, setAddedRowIndex] = useState<number | null>(null);
  const rows = form.values.ybcRows;

  // Row messages are listed under the table, so a cell only shows its error state.
  const getCellInputProps = (...args: Parameters<typeof wrappedGetYBCRowInputProps>) => {
    const { error, ...rest } = wrappedGetYBCRowInputProps(...args);
    return { ...rest, error: Boolean(error) };
  };

  return (
    <Stack gap="xs">
      <Paper withBorder>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>
                <Flex gap="xs" align="center">
                  Length
                  <InfoIcon label="Straight length before the bend" />
                </Flex>
              </Table.Th>
              <Table.Th>
                <Flex gap="xs" align="center">
                  Rotation
                  <InfoIcon label="Rotation before the bend" />
                </Flex>
              </Table.Th>
              <Table.Th>
                <Flex gap="xs" align="center">
                  Angle
                  <InfoIcon label="Bend angle" />
                </Flex>
              </Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map(({ key }, index) => {
              const isTrailingStraight = index === rows.length - 1;
              return (
                <Table.Tr key={key} className="hover-title">
                  <Table.Td>
                    <InchesInput
                      aria-label={`Length, row ${index + 1}`}
                      autoFocus={index === addedRowIndex}
                      {...getCellInputProps(`ybcRows.${index}.straightLength`)}
                    />
                  </Table.Td>
                  <Table.Td>
                    <NumberInputWrapped
                      aria-label={`Rotation, row ${index + 1}`}
                      suffix="°"
                      hideControls
                      min={-180}
                      max={180}
                      clampBehavior="strict"
                      decimalScale={3}
                      {...getCellInputProps(`ybcRows.${index}.rotation`)}
                    />
                  </Table.Td>
                  <Table.Td>
                    <NumberInputWrapped
                      aria-label={`Angle, row ${index + 1}`}
                      suffix="°"
                      hideControls
                      min={0}
                      max={90}
                      clampBehavior="strict"
                      decimalScale={3}
                      disabled={isTrailingStraight}
                      {...getCellInputProps(`ybcRows.${index}.angle`)}
                    />
                  </Table.Td>
                  <Table.Td>
                    {!isTrailingStraight && rows.length > MINIMUM_ROWS && (
                      <ActionIcon
                        color="red"
                        size="xs"
                        className="hover-title--on"
                        onClick={() => {
                          form.removeListItem('ybcRows', index);
                          scheduleValidation();
                        }}
                      >
                        <EvolveIcon icon="Delete" size="sm" />
                      </ActionIcon>
                    )}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Paper>
      {[
        ...new Set(
          strongEntries(form.errors)
            .filter(([path]) => path.startsWith('ybcRows'))
            .map(([, error]) => error)
            .filter((error) => typeof error === 'string'),
        ),
      ].map((message) => (
        <Text key={message} c="red" fz="sm">
          {message}
        </Text>
      ))}
      <AgGridStyleTooltip
        label={`A custom bend can have at most ${MAXIMUM_ROWS} rows`}
        hidden={rows.length < MAXIMUM_ROWS}
        withArrow
        openDelay={0}
      >
        <Button
          variant="subtle"
          leftSection={<EvolveIcon icon="Add" />}
          disabled={rows.length >= MAXIMUM_ROWS}
          onClick={() => {
            const insertionIndex = rows.length - 1;
            setAddedRowIndex(insertionIndex);
            form.insertListItem('ybcRows', blankYbcRow(), insertionIndex);
            scheduleValidation();
          }}
        >
          Add row
        </Button>
      </AgGridStyleTooltip>
    </Stack>
  );
};
